import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

import { useHasLogtoRole } from '@/hooks/useHasLogtoRole';
import { loadStoredAppearance, resolvePalette, type AppearancePrefs, type PaletteEstesa } from '@/lib/appearance';
import type { PropsRigaAzione } from '@/components/layout/AzionePersistente';
import type { PropsModaleEsito } from '@/components/layout/ModaleEsito';
import type { VistaNews } from '@/lib/vistaNews';
import { useAppSelector } from '@/store/hooks';
import { PALETTE_V3 as DATI_PALETTE_V3 } from '@/v3/palette';
import type { DatiIncontro } from '@/v3/chrome/incontro/dati';
import { serveUscitaV3 as SERVE_USCITA_V3, showsTabBarV3 as SHOWS_TAB_BAR_V3 } from '@/v3/rotteBarra';

/**
 * LA V3 — il predicato, in UN file.
 *
 * `useV3()` e' vero se e solo se valgono TUTTE E TRE:
 *   (a) il flag di build `VITE_FEATURE_V3_ENABLED` (`true` in `.env.staging`, `false` in `.env.production`);
 *   (b) il ruolo Logto `beta` (`useHasLogtoRole('beta')`, come `RoleGate`);
 *   (c) l'interruttore «Prova la v3» in Impostazioni, SPENTO di default (`AppearancePrefs.v3`, salvato con le
 *       preferenze d'aspetto).
 * L'interruttore lo vede solo chi ha (a) e (b): `useV3Disponibile()`.
 *
 * ⛔ QUESTO E' L'UNICO FILE CHE LEGGE `VITE_FEATURE_V3_ENABLED`, e lo legge con un confronto LETTERALE
 * (`=== 'true'`): e' quello che il bundler sa piegare. In produzione `V3_IN_BUILD` vale `false`, e tutto cio' che e'
 * raggiungibile solo da un `if (V3_IN_BUILD)` (palette, fogli, caratteri, interruttore) sparisce da `dist/`.
 * `tools/verify-v3.ts` lo misura, sul sorgente e sul bundle. Un secondo file che legga il flag, o un confronto non
 * letterale, lo rompe. Attorno a `V3_IN_BUILD` NIENTE import statico di `@/v3/strato` o di `@/v3/InterruttoreV3`.
 *
 * La palette NON sovrascrive il `presetId` salvato: si risolve al momento di applicare (`paletteDaApplicare`).
 */
export const V3_IN_BUILD = import.meta.env.VITE_FEATURE_V3_ENABLED === 'true';

/**
 * I SEGNAPOSTO DEMO della testa dell'Hub (H-1; reference `2026-10-28-v3-hub-testa-h1.md` §7, L-3801 / L-3806, ADR-0258): il banner DEMO e la barra EXP DEMO.
 * Vero solo con la v3 in build E `VITE_V3_DEMO_ENABLED=true`, che sta SOLO in `.env.staging` (`false` dichiarato negli altri tre `.env*`). Un secondo flag perche'
 * il giorno in cui la v3 andasse in produzione i DEMO non la seguano. ⛔ Questo e' l'UNICO file che lo legge, col confronto LETTERALE: lo tiene `tools/verify-v3-demo.ts`.
 */
export const V3_DEMO = V3_IN_BUILD && import.meta.env.VITE_V3_DEMO_ENABLED === 'true';

/** La tavola di verita' del predicato: vero solo con (a), (b) e (c) insieme. Pura, per i test. */
export function v3Attivo(c: { flag: boolean; beta: boolean; interruttore: boolean }): boolean {
  return c.flag && c.beta && c.interruttore;
}

/** (a) AND (b): chi ha diritto di VEDERE l'interruttore. */
export function v3Disponibile(c: { flag: boolean; beta: boolean }): boolean {
  return c.flag && c.beta;
}

function usaV3Disponibile(): boolean {
  return v3Disponibile({ flag: V3_IN_BUILD, beta: useHasLogtoRole('beta') });
}

function usaV3(): boolean {
  const beta = useHasLogtoRole('beta');
  const interruttore = useAppSelector((s) => s.appearance.v3 === true);
  return v3Attivo({ flag: V3_IN_BUILD, beta, interruttore });
}

// In produzione i due hook sono `() => false`: nessuna sottoscrizione ai ruoli in piu' e il codice vero sparisce.
export const useV3Disponibile: () => boolean = V3_IN_BUILD ? usaV3Disponibile : () => false;
export const useV3: () => boolean = V3_IN_BUILD ? usaV3 : () => false;

/** La palette della v3; `undefined` in una build senza v3. */
export const PALETTE_V3: PaletteEstesa | undefined = V3_IN_BUILD ? DATI_PALETTE_V3 : undefined;

/**
 * Le regole della barra nella v3, per `AppShell`: con la barra a quattro voci le schermate senza barra sono altre (ADR-0242 §2), e dove manca la barra serve l'uscita. `undefined` in una
 * build senza v3: l'import statico sopra lo piega la `const` sul flag, e il codice (con i suoi testi) non entra in produzione (riserva di flotta su #766, R-b).
 */
export const showsTabBarV3: ((pathname: string) => boolean) | undefined = V3_IN_BUILD ? SHOWS_TAB_BAR_V3 : undefined;
export const serveUscitaV3: ((pathname: string) => boolean) | undefined = V3_IN_BUILD ? SERVE_USCITA_V3 : undefined;

/** L'interruttore di Impostazioni, caricato a richiesta; `undefined` in una build senza v3. */
export const InterruttoreV3: LazyExoticComponent<ComponentType> | undefined = V3_IN_BUILD
  ? lazy(() => import('@/v3/InterruttoreV3'))
  : undefined;

/** La palette da applicare: quella della v3 se accesa, altrimenti il tema SALVATO (il `presetId` non si tocca mai). */
export function paletteDaApplicare(prefs: AppearancePrefs, v3: boolean): PaletteEstesa {
  return v3 && PALETTE_V3 ? PALETTE_V3 : resolvePalette(prefs);
}

let voluta = false;

/**
 * L'UNICO punto che scrive `data-v3` sulla radice. Accesa: carica prima lo strato (fogli e caratteri), poi scrive
 * l'attributo; spenta: lo toglie subito. Se nel frattempo e' cambiata la volonta', l'ultima vince. Risponde `false` se la v3
 * non si e' applicata (flag spento nella build, o strato non scaricato): mai un'eccezione.
 */
export async function applicaV3(attiva: boolean): Promise<boolean> {
  if (!V3_IN_BUILD) return false;
  voluta = attiva;
  if (attiva) {
    try {
      await import('@/v3/strato');
    } catch {
      // Lo strato non si e' scaricato (rete mobile instabile, chunk sparito dopo un rilascio): la v3 NON si applica e l'app
      // resta com'era. Non e' un errore che deve arrivare al bootstrap: «EnvUnsupported» per una v3 mancata sarebbe un'app
      // morta per chi la v3 nemmeno la vuole. Al prossimo cambio del predicato `useAppearance` riprova.
      if (voluta === attiva) document.documentElement.removeAttribute('data-v3');
      return false;
    }
  }
  if (voluta === attiva) document.documentElement.toggleAttribute('data-v3', attiva);
  return true;
}

/**
 * Prima del primo render, senza ruoli (Logto non ha ancora risposto): (a) AND (c). E' una PRENOTAZIONE: scegliere il
 * colore del chrome nativo e il primo dipinto senza lampo per chi ha gia' acceso la v3. Il predicato vero (con (b))
 * corregge subito dopo in `useAppearance`: chi ha perso `beta` torna al tema salvato.
 */
export function v3Prenotata(prefs: AppearancePrefs): boolean {
  return V3_IN_BUILD && prefs.v3 === true;
}

/** Il colore con cui `initTelegram` dipinge il chrome nativo all'avvio: `undefined` = quello di sempre (nero). */
export function coloreChromeIniziale(prefs: AppearancePrefs = loadStoredAppearance()): string | undefined {
  return v3Prenotata(prefs) ? PALETTE_V3?.['--color-bg'] : undefined;
}

/**
 * Quanto aspetta il boot lo strato della v3 prima di dipingere comunque (flotta su #753, nota 4). Una rete che FALLISCE rifiuta l'`import()` e
 * `applicaV3` risponde `false`; una rete che si BLOCCA non rifiuta e non risolve mai, e senza un tetto il primo render non arriverebbe: app bianca.
 * 2,5 s: oltre, un utente beta vede il tema salvato per un attimo e poi la v3, non una schermata vuota.
 */
export const TETTO_STRATO_MS = 2500;

/**
 * La palette del primo dipinto (main.tsx): prenotata -> carica lo strato e dipinge la v3, altrimenti il tema salvato. Il primo dipinto e' il tema
 * SALVATO anche se lo strato non arriva (errore o tetto scaduto): il boot non muore e non resta fermo. Se l'`import()` arriva dopo il tetto, `applicaV3`
 * lo applica solo se nel frattempo la volonta' e' ancora «accesa» (`useAppearance` la corregge appena sa dei ruoli).
 */
export async function paletteIniziale(prefs: AppearancePrefs, tettoMs: number = TETTO_STRATO_MS): Promise<PaletteEstesa> {
  if (!v3Prenotata(prefs)) return resolvePalette(prefs);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const scaduto = new Promise<false>((risolvi) => {
    timer = setTimeout(() => risolvi(false), tettoMs);
  });
  const arrivato = await Promise.race([applicaV3(true), scaduto]);
  clearTimeout(timer);
  if (!arrivato) return resolvePalette(prefs);
  return paletteDaApplicare(prefs, true);
}

/** La testata della v3 (fase 2): Resources Bar al posto di `AppBanner`, scelta da `AppShell` con `useV3()`. Caricata a richiesta; `undefined` in una build senza v3. */
export const TestataV3: LazyExoticComponent<ComponentType> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/TestataV3').then((m) => ({ default: m.TestataV3 }))) : undefined;

/** Il corpo dell'Hub della v3 (fase 2, PR 8): Game Dropdown, griglia delle modalita' e pannello al centro, scelto da `Hub.tsx` con `useV3()`. Caricato a richiesta; `undefined` in una build senza v3. */
export const HubCorpoV3: LazyExoticComponent<ComponentType> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/HubCorpoV3').then((m) => ({ default: m.HubCorpoV3 }))) : undefined;

/** Il banner DEMO della testa dell'Hub (H-1): fondo neutro, tratteggio e pastiglia «BANNER · DEMO», solo finche' il banner vero non e' collegato. Caricato a richiesta; `undefined` senza `V3_DEMO`. */
export const BannerDemoV3: LazyExoticComponent<ComponentType> | undefined = V3_DEMO ? lazy(() => import('@/v3/chrome/testa/BannerDemo').then((m) => ({ default: m.BannerDemo }))) : undefined;

/** La barra EXP DEMO nella riga delle risorse dell'Hub (H-1, L-3806): etichetta DEMO sulla barra, nessun numero. Caricata a richiesta; `undefined` senza `V3_DEMO`. */
export const BarraExpDemoV3: LazyExoticComponent<ComponentType> | undefined = V3_DEMO ? lazy(() => import('@/v3/chrome/testa/BarraExpDemo').then((m) => ({ default: m.BarraExpDemo }))) : undefined;

/** La barra in basso a quattro voci della v3 (fase 2, PR 5), scelta da `AppShell` con `useV3()`. Caricata a richiesta; `undefined` in una build senza v3. */
export const TabBarV3: LazyExoticComponent<ComponentType> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/TabBarV3').then((m) => ({ default: m.TabBarV3 }))) : undefined;

/** La riga della barra del torneo della v3 (fase 2, PR 9b), passata da `AppShell` ad `AzionePersistente` come `Riga` con `useV3()`. Caricata a richiesta; `undefined` in una build senza v3. */
export const RigaTorneoV3: LazyExoticComponent<ComponentType<PropsRigaAzione>> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/RigaTorneoV3').then((m) => ({ default: m.RigaTorneoV3 }))) : undefined;

/** La partita viva nella stessa barra della v3 (fase 2, PR 9c), scelta da `AppShell` con `useV3()` al posto della `ActiveMatchPill`. Caricata a richiesta; `undefined` in una build senza v3. */
export const PartitaVivaV3: LazyExoticComponent<ComponentType> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/PartitaVivaV3').then((m) => ({ default: m.PartitaVivaV3 }))) : undefined;

/** La schermata di una modalita' della v3 (fase 2, PR 7): la rotta `/modalita/:chiave`. Caricata a richiesta; `undefined` in una build senza v3. */
export const SchermataModalita: LazyExoticComponent<ComponentType> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/SchermataModalita').then((m) => ({ default: m.SchermataModalita }))) : undefined;
/** La pagina del torneo della v3 (fase 3, PR 3), scelta da `LimuloTournamentDetail` con `useV3()`: UN punto solo. Caricata a richiesta; `undefined` in una build senza v3. */
export const TorneoV3: LazyExoticComponent<ComponentType<{ tab?: string }>> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/TorneoV3').then((m) => ({ default: m.TorneoV3 }))) : undefined;
/** La scheda d'incontro della v3 (fase 4, PR 1), scelta da `LimuloMatchDetail` con `useV3()` A VALLE dei dati: UN punto solo. Caricata a richiesta; `undefined` in una build senza v3. */
export const SchedaIncontroV3: LazyExoticComponent<ComponentType<{ dati: DatiIncontro }>> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/incontro/SchedaIncontroV3').then((m) => ({ default: m.SchedaIncontroV3 }))) : undefined;
/** Le schermate aperte dai riquadri della pagina del torneo (fase 3, PR 2): la rotta `/limulo/tournaments/:id/vista/:quale`. Caricata a richiesta; `undefined` in una build senza v3. */
export const VistaTorneoV3: LazyExoticComponent<ComponentType> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/VistaTorneoV3').then((m) => ({ default: m.VistaTorneoV3 }))) : undefined;
/** La modale degli esiti della v3 (fase 4, PR 5), scelta da `Modale` (`ModaleEsito.tsx`) con `useV3()`: UN punto solo. Caricata a richiesta; `undefined` in una build senza v3. */
export const ModaleEsitoV3: LazyExoticComponent<ComponentType<PropsModaleEsito>> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/esiti/ModaleEsitoV3').then((m) => ({ default: m.ModaleEsitoV3 }))) : undefined;
/** L'uscita delle schermate senza barra della v3 (fase 2, PR 6), scelta da `AppShell` con `useV3()`. Caricata a richiesta; `undefined` in una build senza v3. */
export const UscitaV3: LazyExoticComponent<ComponentType> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/UscitaV3').then((m) => ({ default: m.UscitaV3 }))) : undefined;

/** Il corpo delle News della v3 (v3 «Social e Inventario», PR-A), scelto da `News.tsx` con `useV3()` su una `VistaNews` gia' calcolata: UN punto solo. Caricato a richiesta; `undefined` in una build senza v3. */
export const NewsV3: LazyExoticComponent<ComponentType<VistaNews>> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/chrome/social/news/NewsV3').then((m) => ({ default: m.NewsV3 }))) : undefined;

/** Lo strato che dice, in una riga, che con la v3 accesa i temi non valgono. Caricato a richiesta; `undefined` in una build senza v3. */
export const NotaTemiV3: LazyExoticComponent<ComponentType> | undefined = V3_IN_BUILD ? lazy(() => import('@/v3/NotaTemiV3')) : undefined;

/**
 * IL FEEDBACK nella v3 (tma-orcr-98; reference `2026-10-30-v3-feedback-flusso.md`): il pulsante flottante che, dentro la v3, prende il posto di quello dei minigiochi e segnala cio' che si sta vedendo.
 * 🔴 FLAG LETTERALE `VITE_FEATURE_FEEDBACK_ENABLED` (acceso SOLO in `.env.staging`), letto QUI e in nessun altro file, e scritto per esteso in ogni punto che decide un chunk o un marcatore: il bundler piega
 * il confronto, e in produzione non resta ne' il chunk, ne' snapdom, ne' il marcatore (ADR-0229 §5; trappola di `lib/minigiochi.ts`: una chiamata di funzione non si piega). In AND con `V3_IN_BUILD`;
 * il ruolo `beta` e l'interruttore li porta `useV3()` (punto (b) e (c) del predicato), che decide il montaggio in `GuscioFeedbackProtetto`. `tools/verify-feedback.ts` lo misura, sul sorgente e sul bundle.
 */
export const FeedbackV3: LazyExoticComponent<ComponentType> | undefined =
  V3_IN_BUILD && import.meta.env.VITE_FEATURE_FEEDBACK_ENABLED === 'true' ? lazy(() => import('@/v3/chrome/feedback/FeedbackV3').then((m) => ({ default: m.FeedbackV3 }))) : undefined;

/**
 * Il MARCATORE dell'oscuramento automatico: `<elemento {...OSCURA}>` sui nodi che portano dati sensibili (saldi, nomi e avatar altrui, testo delle chat, codici, e-mail; elenco in `tools/feedback-oscura-dichiarati.json`).
 * Vale `{}` quando il flag non e' acceso (produzione): la stringa `data-feedback-oscura` non esce dal bundle e il DOM di produzione non cambia. In staging e' un attributo INERTE anche con la v3 spenta (Q3).
 */
export const OSCURA: Readonly<Record<string, string>> =
  V3_IN_BUILD && import.meta.env.VITE_FEATURE_FEEDBACK_ENABLED === 'true' ? { 'data-feedback-oscura': '' } : {};
